import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useMe } from '../auth';
import { Empty, ErrorAlert, Loading } from '../components/ui';
import { dateTime } from '../format';
import { isUnsynced, useLocalDrafts } from '../offline';
import { PrepareMessageButton } from './Mensagens';

// Alertas internos e fila de lembretes sugeridos (seções 15 e 16).
// Alertas encerram sozinhos quando a pendência é resolvida; lembretes ao cliente só saem com envio manual.

export interface AlertRow {
  id: string;
  kind: string;
  kindLabel: string;
  title: string;
  detail: string | null;
  link: string | null;
  severity: 'info' | 'atencao' | 'critico';
  occurredAt: string;
  readAt: string | null;
  resolvedAt: string | null;
  emailedAt: string | null;
}

export interface ReminderSuggestion {
  appointmentId: string;
  title: string;
  startsAt: string;
  clientName: string | null;
  offsetHours: number;
  dueAt: string;
  contactCount: number;
}

const offsetLabel = (h: number) => (h % 24 === 0 ? (h === 24 ? 'véspera (24 h antes)' : `${h / 24} dias antes`) : `${h} h antes`);

export function AlertsPage() {
  const me = useMe();
  const qc = useQueryClient();
  const [recentes, setRecentes] = useState(false);
  const q = useQuery({ queryKey: ['alerts', recentes], queryFn: () => api.get<{ data: AlertRow[] }>(`/alerts${recentes ? '?recentes=1' : ''}`), refetchInterval: 60_000 });
  const unsynced = useLocalDrafts(me.id).filter(isUnsynced);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['alerts'] });
    void qc.invalidateQueries({ queryKey: ['alerts-summary'] });
  };
  const read = useMutation({ mutationFn: (id: string) => api.post(`/alerts/${id}/read`), onSuccess: refresh });
  const readAll = useMutation({ mutationFn: () => api.post('/alerts/read-all'), onSuccess: refresh });
  const rows = q.data?.data ?? [];
  const unread = rows.filter((a) => !a.readAt && !a.resolvedAt).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Alertas e lembretes</h1>
          <p>Pendências internas suas. Elas encerram sozinhas quando resolvidas. Nada aqui envia mensagem ao cliente.</p>
        </div>
        <div className="actions">
          <Link className="btn" to="/configuracoes?tab=alertas">
            Preferências de alertas
          </Link>
        </div>
      </div>

      <ReminderQueue />

      {unsynced.length > 0 && (
        <section className="card sev-atencao">
          <h2>Sincronização pendente</h2>
          <p className="small">
            {unsynced.length} rascunho(s) estão só neste aparelho. Com conexão, eles são enviados automaticamente; abra para conferir.{' '}
            <Link to="/execucoes">Ver rascunhos</Link>
          </p>
        </section>
      )}

      <section className="card">
        <div className="card-head">
          <h2>Alertas {unread > 0 && <span className="badge badge-demo">{unread} não lido(s)</span>}</h2>
          <div className="actions">
            <label className="check small">
              <input type="checkbox" checked={recentes} onChange={(e) => setRecentes(e.target.checked)} /> Mostrar encerrados (7 dias)
            </label>
            {unread > 0 && (
              <button className="btn btn-sm" disabled={readAll.isPending} onClick={() => readAll.mutate()}>
                Marcar todos como lidos
              </button>
            )}
          </div>
        </div>
        <ErrorAlert error={q.error ?? read.error ?? readAll.error} />
        {q.isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>Nenhuma pendência aberta.</Empty>
        ) : (
          <div className="list">
            {rows.map((a) => (
              <div key={a.id} className={`list-item alert-row sev-${a.severity}${!a.readAt && !a.resolvedAt ? ' unread' : ''}${a.resolvedAt ? ' resolved' : ''}`}>
                <div className="body">
                  <div className="title">{a.link ? <Link to={a.link}>{a.title}</Link> : a.title}</div>
                  {a.detail && <div className="small">{a.detail}</div>}
                  <div className="meta">
                    <span className="badge">{a.kindLabel}</span>
                    <span>desde {dateTime(a.occurredAt)}</span>
                    {a.emailedAt && <span>avisado por e-mail</span>}
                    {a.resolvedAt && <span>encerrado em {dateTime(a.resolvedAt)}</span>}
                  </div>
                </div>
                {!a.readAt && !a.resolvedAt && (
                  <button className="btn btn-sm btn-ghost" disabled={read.isPending} onClick={() => read.mutate(a.id)}>
                    Marcar como lido
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="small muted" style={{ marginTop: '0.75rem' }}>
          “Lido” só para de destacar o alerta; ele continua aberto até a pendência ser resolvida.
        </p>
      </section>
    </>
  );
}

// Fila de lembretes ao cliente sugeridos pela agenda. Preparar abre o fluxo normal: conferir e confirmar o envio.
export function ReminderQueue({ compact = false }: { compact?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['reminders'], queryFn: () => api.get<{ data: ReminderSuggestion[] }>('/reminders/suggested'), refetchInterval: 60_000 });
  const dismiss = useMutation({
    mutationFn: (s: ReminderSuggestion) => api.post('/reminders/dismiss', { appointmentId: s.appointmentId, offsetHours: s.offsetHours }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reminders'] });
      void qc.invalidateQueries({ queryKey: ['alerts-summary'] });
    },
  });
  const list = q.data?.data ?? [];
  if (compact && list.length === 0) return null;
  return (
    <section className="card">
      <h2>Lembretes sugeridos ao cliente {list.length > 0 && <span className="badge badge-brand">{list.length}</span>}</h2>
      <p className="small muted">Sugestões pela agenda. Nada é enviado sem você preparar, conferir e confirmar.</p>
      <ErrorAlert error={q.error ?? dismiss.error} />
      {q.isLoading ? (
        <Loading />
      ) : list.length === 0 ? (
        <Empty>Nenhum lembrete sugerido agora.</Empty>
      ) : (
        <div className="list">
          {list.map((s) => (
            <div key={`${s.appointmentId}:${s.offsetHours}`} className="list-item alert-row">
              <div className="body">
                <div className="title">
                  <Link to={`/agenda/${s.appointmentId}`}>{s.title}</Link>
                </div>
                <div className="meta">
                  <span>{s.clientName}</span>
                  <span>{dateTime(s.startsAt)}</span>
                  <span className="badge">{offsetLabel(s.offsetHours)}</span>
                  {s.contactCount === 0 && <span className="badge badge-demo">sem contato no compromisso</span>}
                </div>
              </div>
              <div className="actions">
                <PrepareMessageButton label="Preparar lembrete" className="btn btn-sm btn-primary" body={{ purpose: 'lembrete', appointmentId: s.appointmentId }} />
                <button className="btn btn-sm btn-ghost" disabled={dismiss.isPending} onClick={() => dismiss.mutate(s)}>
                  Dispensar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
